import icons from './icons';
import { MenuItem, Dropdown } from './menu/index.js';
import { buildMenuItems } from './setup/index.js';

import { lift } from 'prosemirror-commands';
import { undo, redo } from 'prosemirror-history';
import _ from 'lodash';
import { filter, identity, pickBy } from 'lodash/fp';

import { activateLinkEditor, activateMediaManager } from './custom_commands';

const filterEmptyItems = filter(identity);

function overrideDefaults(defaultSpecs, ourSpecs) {
   return _.mapValues(defaultSpecs, (defaultSpec, specName) => ({
      ...defaultSpec.spec,
      ...ourSpecs[specName],
   }));
}

// MenuItemSpecs that are supplied by ProseMirror, but overriden by us.
const prosemirrorMenuOverrides = {
   toggleStrong: {
      icon: { dom: icons.bold },
      /* Translators: Bold, as in the text formatting */
      title: _js('Bold'),
      shortcut: 'b',
   },
   toggleEm: {
      icon: { dom: icons.italic },
      title: _js('Italic'),
      shortcut: 'i',
   },
   toggleCode: {
      icon: { dom: icons.code },
      /* Translators: "Code" as in monospaced font */
      title: _js('Insert code'),
      shortcut: '`',
   },
   toggleLink: {
      icon: { dom: icons.link },
      title: _js('Insert a link'),
      run: activateLinkEditor,
      execEvent: 'click',
      shortcut: 'k',
      enable: state => {
         const markType = state.schema.marks.link;

         if (state.selection.empty) {
            if (!markType.isInSet(state.selection.$from.marks())) {
               return false;
            }
         }

         return true;
      },
   },
   insertImage: {
      icon: { dom: icons.image },
      title: _js('Insert an image'),
      run: activateMediaManager,
      enable: state => true,
   },
   makeHead2: {
      /* Translators: "header" as in the text at the top of a page */
      icon: { text: _js('Header') },
      title: _js('Make header'),
      class: 'menubar-header',
   },
   makeHead3: {
      /* Translators: "subheader" as in a secondary header on the top of a page */
      icon: { text: _js('Subheader') },
      title: _js('Make subheader'),
      class: 'menubar-subheader',
   },
   makeCodeBlock: {
      /* Translators: "code" as in monospaced font */
      icon: { text: _js('Code') },
      /* Translators: "code block" as in a block of text in monospaced font */
      title: _js('Make code block'),
      class: 'menubar-code',
   },
   makeParagraph: {
      /* Translators: "paragraph" as in regular text */
      icon: { text: _js('Paragraph') },
      select: state => state.schema.nodes.heading,
      title: _js('Make paragraph text'),
      class: 'menubar-paragraph',
   },
   wrapBulletList: {
      icon: { dom: icons.list },
      title: _js('Add a bulleted list'),
   },
   wrapOrderedList: {
      icon: { dom: icons.numbered },
      title: _js('Add a numbered list'),
   },
   wrapBlockQuote: {
      icon: { dom: icons.quote },
      title: _js('Insert a quote'),
   },
};

/**
 * A dictionary of custom menu items that don't exist within
 * prosemirror's default set.
 *
 * Values of this object should follow the MenuItemSpec
 * defined in prosemirror-menu.
 *
 * They should implement a `shouldApply` method that
 * takes a schema object and returns true for whether or not
 * it should be included in the menu bar.
 */
const customMenuSpecs = {
   insertVideo: {
      title: _js('Insert a YouTube or Vimeo Video'),
      icon: { dom: icons.video },
      shouldApply: schema => Boolean(schema.nodes.video),
      select: state => state.selection.empty,
      run: function(state, dispatch, view) {
         Modal.open({
            type: 'form',
            defaultWidth: true,
            form: FormLibrary.create({
               id: 'wikiTextEditorVideoForm',
               class: 'wikiTextEditorModal',
               fields: {
                  videoUrl: {
                     label: _js('YouTube or Vimeo Video URL'),
                     type: 'text',
                     properties: { id: 'wikiTextEditorVideoURLEntry' },
                  },
                  videoCaption: {
                     label: _js('Video Caption (optional)'),
                     type: 'text',
                     properties: { id: 'wikiTextEditorVideoTextEntry' },
                  },
               },
               submit: _js('Insert video'),
            }),
            onSubmit: function(form) {
               const caption = form.videoCaption ? ']' + form.videoCaption + '[/video]' : ']';

               if (!/https?:\/\//.test(form.videoUrl)) {
                  form.videoUrl = 'http://' + form.videoUrl;
               }

               const tr = state.tr.insertText('[video|' + form.videoUrl + caption);
               dispatch(tr);
            },
         });
      },
   },
   undoAction: {
      title: _js('Undo'),
      icon: { dom: icons.undo },
      shortcut: 'z',
      run: undo,
      enable: state => undo(state),
      shouldApply: schema => true,
   },
   redoAction: {
      title: _js('Redo'),
      icon: { dom: icons.redo },
      shortcut: '⇧z',
      run: redo,
      enable: state => redo(state),
      shouldApply: schema => true,
   },
   liftItem: {
      title: _js('Decrease indent'),
      icon: { dom: icons.lift },
      run: lift,
      select: state => lift(state),
      shouldApply: schema => true,
   },
};

/**
 * Builds a list of MenuItems that are appropriate for the given schema.
 */
export default function buildMenu(schema) {
   const filterCustomItems = pickBy(spec => spec.shouldApply(schema));
   const builtinMenuItems = overrideDefaults(buildMenuItems(schema), prosemirrorMenuOverrides);
   const menuSpecs = {
      ...builtinMenuItems,
      ...filterCustomItems(customMenuSpecs),
   };

   const {
      makeHead2,
      makeHead3,
      makeCodeBlock,
      toggleStrong,
      toggleEm,
      toggleLink,
      toggleCode,
      wrapBlockQuote,
      wrapBulletList,
      wrapOrderedList,
      insertImage,
      insertVideo,
      makeParagraph,
      liftItem,
      undoAction,
      redoAction,
   } = _.mapValues(menuSpecs, spec => new MenuItem(spec));

   const inlineMenu = [toggleStrong, toggleEm, toggleCode, toggleLink];
   const blockTypeMenu = [makeHead2, makeHead3, makeParagraph, makeCodeBlock];
   const blockMenu = [wrapBlockQuote, wrapBulletList, wrapOrderedList, liftItem];
   const mediaMenu = [insertImage, insertVideo];
   const timelineMenu = [undoAction, redoAction];

   const menu = [
      filterEmptyItems(inlineMenu),
      [new Dropdown(filterEmptyItems(blockTypeMenu), { label: icons.paragraph })],
      filterEmptyItems(blockMenu),
      filterEmptyItems(mediaMenu),
      filterEmptyItems(timelineMenu),
   ];

   return menu.filter(x => x.length);
}
